import React, { useEffect, useMemo } from 'react';
import { Button, Drawer, Form, Input, message, Select, Space } from 'antd';
import { useSnapshot } from 'valtio';
import { changeGradeApi } from '@/api/member/member';
import { state } from '@/store';

interface LevelOption {
  label: string;
  value: string | number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  levelOptions: LevelOption[];
  oneId: string;
  mobile?: string;
  memberCardId?: string | number;
  currentLevelCode?: number | string;
  currentLevelName?: string;
  shopId?: string;
  onSuccess?: () => void;
}

const ChangeGradeDrawer: React.FC<Props> = ({
  open,
  onClose,
  levelOptions,
  oneId,
  mobile,
  memberCardId,
  currentLevelCode,
  currentLevelName,
  shopId,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const snap = useSnapshot(state);
  const currentShopValue = String(shopId || snap.currentShopId);
  const mergedLevelOptions = useMemo(() => {
    if (!currentLevelCode && currentLevelName) {
      const exists = levelOptions.some(option => option.label === currentLevelName);
      if (!exists) {
        return [{ label: currentLevelName, value: currentLevelName }, ...levelOptions];
      }
    }
    return levelOptions;
  }, [levelOptions, currentLevelCode, currentLevelName]);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        oneId,
        mobile: mobile || '-',
        memberCard: currentShopValue,
        beforeLevel: currentLevelCode ?? currentLevelName ?? undefined,
        afterLevel: undefined,
        remark: '',
      });
    }
  }, [open, oneId, mobile, currentLevelCode, currentLevelName, currentShopValue, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const res = await changeGradeApi({
        memberCardId,
        oneId,
        shopId: shopId || snap.currentShopId,
        beforeGradeCode: currentLevelCode,
        afterGradeCode: values.afterLevel,
        changeCause: '手动变更',
        remark: values.remark,
      });
      if (res?.code === 200) {
        message.success('等级调整成功');
        onClose();
        onSuccess?.();
      }
    } catch (error: any) {
      if (error?.errorFields) return;
    }
  };

  return (
    <Drawer
      title="等级变更"
      width={420}
      destroyOnHidden
      open={open}
      onClose={onClose}
      extra={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            onClick={handleSubmit}>
            确定
          </Button>
        </Space>
      }>
      <Form
        layout="vertical"
        form={form}>
        <Form.Item
          label="OneID"
          name="oneId">
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="会员手机号"
          name="mobile">
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="会员卡"
          name="memberCard">
          <Select
            disabled
            options={snap.shopList?.map((item: any) => ({ label: item.shopName, value: String(item.shopId) }))}
          />
        </Form.Item>
        <Form.Item
          label="当前等级"
          name="beforeLevel">
          <Select
            disabled
            placeholder="-"
            options={mergedLevelOptions}
          />
        </Form.Item>
        <Form.Item
          label="变更后等级"
          name="afterLevel"
          rules={[{ required: true, message: '请选择变更后等级' }]}>
          <Select
            placeholder="请选择"
            options={levelOptions}
          />
        </Form.Item>
        <Form.Item
          label="备注"
          name="remark"
          rules={[{ required: true, message: '请输入备注' }]}>
          <Input.TextArea
            rows={3}
            maxLength={200}
            showCount
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default ChangeGradeDrawer;
